import React, { useState } from 'react';
import { TrashIcon, ExclamationTriangleIcon, InformationCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

// Google Form URL
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdNc5zq9p1z8Xf32LHcPTURTRxz23jPYEBZlozZbTiyPN75jw/viewform';

const DeleteAccount: React.FC = () => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleRedirectToForm = () => {
    if (confirmDelete) {
      window.open(GOOGLE_FORM_URL, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <TrashIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Delete My Account</h1>
          <p className="text-xl text-gray-600">Request to permanently delete your Rightoz account</p>
        </div>

        {/* Warning Alert */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">Important Information</h3>
              <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                <li>Account deletion is permanent and cannot be undone</li>
                <li>All your data, including order history, subscriptions, and preferences will be permanently deleted</li>
                <li>You will lose access to all Rightoz services</li>
                <li>If you have active subscriptions, they will be cancelled</li>
                <li>This process may take up to 30 days to complete</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Information Section */}
          <section>
            <div className="flex items-start mb-4">
              <InformationCircleIcon className="h-6 w-6 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <h2 className="text-2xl font-bold text-gray-900">Before You Proceed</h2>
            </div>
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <p className="text-gray-700 leading-relaxed mb-4">
                We're sorry to see you go! Before deleting your account, consider these alternatives:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
                <li><strong>Pause your subscription:</strong> You can pause your subscription instead of deleting your account</li>
                <li><strong>Update preferences:</strong> Adjust your delivery schedule or preferences to better suit your needs</li>
                <li><strong>Contact support:</strong> Our support team can help resolve any issues you're experiencing</li>
                <li><strong>Download your data:</strong> Request a copy of your data before deletion</li>
              </ul>
            </div>
          </section>

          {/* Confirmation and Redirect Section */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Ready to Delete Your Account?</h2>
            
            {/* Confirmation Checkbox */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                  className="mt-1 mr-3 h-5 w-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-gray-700">
                  <strong className="text-red-600">I understand</strong> that this action is permanent and cannot be undone. 
                  I confirm that I want to delete my Rightoz account and all associated data.
                </span>
              </label>
            </div>

            {/* Redirect Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleRedirectToForm}
                disabled={!confirmDelete}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                Continue to Deletion Form
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </button>
              <a
                href="/"
                className="flex-1 bg-gray-200 text-gray-800 px-8 py-4 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300 text-center flex items-center justify-center"
              >
                Cancel
              </a>
            </div>
            
            <p className="mt-4 text-sm text-gray-500 text-center">
              You will be redirected to Google Forms to complete your account deletion request.
            </p>
          </section>

          {/* Alternative Contact Section */}
          <section className="border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions or need assistance, our support team is here to help:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <span className="mr-2">📧</span>
                  <strong>Email:</strong> <a href="mailto:info@rightoz.com" className="text-green-600 hover:underline ml-2">info@rightoz.com</a>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">📱</span>
                  <strong>Phone:</strong> <a href="tel:+919114491158" className="text-green-600 hover:underline ml-2">+91 91144 91158</a>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">💬</span>
                  <strong>Support Hours:</strong> <span className="ml-2">Monday - Friday, 9 AM - 6 PM IST</span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
